/**
 * Shared New Call / Call Setup form state. Used by the console drawer and /call page.
 */

import { useEffect, useMemo, useState, type FormEvent, type KeyboardEvent } from "react";

import {
  catalogDeskFeatures,
  getIvrScript,
  getScriptCatalog,
  graphHasCollect,
  graphHasStart,
  graphIsSparse,
  validateScript,
  type CatalogDeskFeatures,
} from "../features/ivr/api";
import { ApiError, api, request, type CidKind } from "./api";
import {
  FALLBACK_COUNTRIES,
  countriesForSip,
  countryByIso,
  inferCountry,
  parseCountryList,
  suggestLanguage,
  trunkCountryBlock,
  type DialCountry,
} from "./country";
import {
  formToScriptValue,
  mergeScriptOptions,
  parseScriptValue,
  scriptValueToForm,
  type ScriptOption,
} from "./scriptOptions";
import { useStore } from "./store";
import type { CallProfile, CallSetup, PackInfo, SipProfile } from "./types";
import { STAGE_FILES, SPOKEN_LANGUAGES } from "./languages";

export const SCRIPT_NOT_READY_HINT = "Script not ready — open Design";

interface LanguagesBody {
  languages?: Array<{ code?: string; label?: string }>;
  country_language?: unknown;
}

/** ISO2 → code from GET /api/languages, or null when that field is absent. */
function parseCountryLanguage(raw: unknown): Record<string, string> | null {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return null;
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw)) {
    const iso = key.trim().toUpperCase();
    const code = typeof value === "string" ? value.trim().toLowerCase() : "";
    if (!iso || !code) continue;
    out[iso] = code;
  }
  return out;
}

/**
 * Language the desk should show for a country. Server map wins when present.
 * Absent map keeps the local suggestLanguage hint — no second country table.
 */
function hintedLanguage(
  iso2: string,
  codes: readonly string[],
  countryLanguage: Record<string, string> | null,
): string {
  const iso = iso2.trim().toUpperCase();
  if (!iso) return "";
  if (countryLanguage) return countryLanguage[iso] ?? "";
  return suggestLanguage(iso, codes);
}

async function loadLanguageCatalog(): Promise<{
  languages: Array<{ code: string; label: string }>;
  countryLanguage: Record<string, string> | null;
}> {
  const data = await request<LanguagesBody>("/api/languages");
  const languages = (data.languages ?? [])
    .map((item) => ({
      code: String(item.code ?? "").trim(),
      label: String(item.label ?? item.code ?? "").trim(),
    }))
    .filter((item) => item.code);
  return { languages, countryLanguage: parseCountryLanguage(data.country_language) };
}

export const CALL_SETUP_ADVANCE = [
  "profile",
  "dest",
  "name",
  "cid",
  "sip",
  "speak_as",
  "business",
  "purpose",
  "digits",
  "known_otp",
  "script",
  "line",
  "country",
  "language",
] as const;

export interface CallSetupFormState {
  profile: string;
  dest: string;
  name: string;
  cid: string;
  sip: string;
  speak_as: string;
  business: string;
  purpose: string;
  digits: string;
  known_otp: string;
  pack: string;
  ivr_id: string;
  stage: string;
  line: string;
  language: string;
  country: string;
  allowed_countries: string;
  script_vars: Record<string, string>;
  identity_replay: boolean;
  avmd: boolean;
  record: boolean;
  scripts: Record<string, string>;
}

export const EMPTY_CALL_SETUP: CallSetupFormState = {
  profile: "",
  dest: "",
  name: "",
  cid: "",
  sip: "",
  speak_as: "",
  business: "",
  purpose: "",
  digits: "6",
  known_otp: "",
  pack: "default",
  ivr_id: "",
  stage: "identification.txt",
  // Pre-fill a sensible per-call identification line for the default pack so
  // Dial never originates with an empty `line` overlay. Avoids the
  // script_compile_failed → empty spoken body path when the operator has not
  // typed their own intro. Operators can still clear the field if they want
  // the on-disk identification.txt to play verbatim.
  line: "",
  language: "en",
  country: "",
  allowed_countries: "",
  script_vars: {},
  identity_replay: true,
  avmd: true,
  record: true,
  scripts: {},
};

/**
 * Map an originate payload onto the form.
 */
export function setupToForm(setup: CallSetup): Partial<CallSetupFormState> {
  return {
    dest: setup.dest ?? "",
    name: setup.customer_name ?? "",
    cid: setup.cli ?? "",
    sip: setup.sip_gateway_id ?? "",
    speak_as: setup.caller_name ?? "",
    business: setup.business_name ?? "",
    purpose: setup.purpose ?? "",
    digits: String(setup.otp_length ?? 6),
    known_otp: setup.expected_code ?? "",
    pack: setup.pack ?? "default",
    ivr_id: setup.ivr_id ?? "",
    language: setup.spoken_language ?? "en",
    country: setup.country ?? "",
    allowed_countries: (setup.allowed_countries ?? []).join(", "),
    script_vars: setup.script_vars ?? {},
    identity_replay: setup.identity_replay ?? true,
    avmd: setup.avmd_enabled ?? true,
    record: setup.recordings_enabled ?? true,
    scripts: setup.scripts ?? {},
    line: (setup.scripts ?? {})["identification.txt"] ?? setup.script ?? "",
    stage: "identification.txt",
    profile: setup.profile_name ?? "",
  };
}

/**
 * Map a saved profile onto the form.
 */
export function profileToForm(profile: CallProfile): Partial<CallSetupFormState> {
  return {
    profile: profile.name,
    dest: profile.dest ?? "",
    name: profile.customer_name ?? "",
    cid: profile.cli ?? "",
    sip: profile.sip_gateway_id ?? "",
    speak_as: profile.caller_name ?? "",
    business: profile.business_name ?? "",
    purpose: profile.purpose ?? "",
    digits: String(profile.otp_length ?? 6),
    known_otp: profile.expected_code ?? "",
    pack: profile.pack ?? "default",
    ivr_id: String(profile.ivr_id ?? ""),
    language: profile.lang || profile.spoken_language || "en",
    country: profile.country ?? "",
    allowed_countries: (profile.allowed_countries ?? []).join(", "),
    script_vars: profile.script_vars ?? {},
    identity_replay: profile.identity_replay ?? true,
    avmd: profile.avmd_enabled ?? true,
    record: profile.recordings_enabled ?? true,
    scripts: profile.scripts ?? {},
    line: (profile.scripts ?? {})["identification.txt"] ?? "",
    stage: "identification.txt",
  };
}

function buildScripts(form: CallSetupFormState): Record<string, string> {
  const scripts = { ...form.scripts };
  if (form.line.trim()) scripts[form.stage] = form.line;
  return scripts;
}

/**
 * Build the originate body from the current form.
 */
export function formToCallSetup(
  form: CallSetupFormState,
  otpLength: number,
  languageTouched = false,
): CallSetup & { lang: string } {
  const scripts = buildScripts(form);
  // Untouched stays blank so the server derives from dest. A pick, including en, is sent.
  const spoken = languageTouched ? form.language.trim() : "";
  return {
    dest: form.dest.trim(),
    customer_name: form.name.trim(),
    cli: form.cid.trim(),
    sip_gateway_id: form.sip || "",
    caller_name: form.speak_as.trim(),
    business_name: form.business.trim(),
    purpose: form.purpose.trim() || null,
    otp_length: otpLength,
    expected_code: form.known_otp.trim() || null,
    pack: form.pack || "default",
    scripts: Object.keys(scripts).length ? scripts : {},
    spoken_language: spoken,
    lang: spoken,
    identity_replay: form.identity_replay,
    avmd_enabled: form.avmd,
    recordings_enabled: form.record,
    profile_name: form.profile.trim(),
    ivr_id: form.ivr_id.trim(),
  };
}

export interface UseCallSetupOptions {
  /** When false, catalogs are not fetched (closed drawer). */
  active: boolean;
  prefill?: CallSetup | null;
  onDialed?: () => void;
}

/**
 * Call-setup form controller. Enter advances; Digits or Dial submits.
 */
export function useCallSetup(options: UseCallSetupOptions) {
  const { active, prefill, onDialed } = options;
  const { run, toast } = useStore();
  const [form, setForm] = useState<CallSetupFormState>(EMPTY_CALL_SETUP);
  const [profiles, setProfiles] = useState<string[]>([]);
  const [packs, setPacks] = useState<PackInfo[]>([{ slug: "default", label: "default" }]);
  const [ivrs, setIvrs] = useState<{ slug: string; name: string }[]>([]);
  const [sips, setSips] = useState<SipProfile[]>([]);
  const [busy, setBusy] = useState(false);
  const [scriptReady, setScriptReady] = useState(true);
  const [scriptReadyReason, setScriptReadyReason] = useState<string | null>(null);
  const [scriptHasCollect, setScriptHasCollect] = useState(true);
  const [scriptChecking, setScriptChecking] = useState(false);
  const [languages, setLanguages] = useState(SPOKEN_LANGUAGES);
  const [countryLanguage, setCountryLanguage] = useState<Record<string, string> | null>(null);
  const [languageTouched, setLanguageTouched] = useState(false);
  /** Empty until the operator picks a CID country. Then dest country must not overwrite it. */
  // shortcut: choosing the country already shown does not fire change, so it does not pin; pick a different country to lock it.
  const [cidCountryPick, setCidCountryPick] = useState("");
  const [cidKind, setCidKindState] = useState<CidKind>("optimal");
  /** null = kinds unknown or GET failed — class toggles stay enabled. {} = no rate sheet. */
  const [cliKinds, setCliKinds] = useState<Record<string, string[]> | null>(null);
  const [countries, setCountries] = useState<DialCountry[]>([...FALLBACK_COUNTRIES]);
  const [packFiles, setPackFiles] = useState<string[]>([...STAGE_FILES]);
  /** Existing pack stage lines (screening_response.txt …) so the New Call
   *  line box shows what is already on disk — not a blank editor. */
  const [packLines, setPackLines] = useState<Record<string, string>>({});
  const [catalogFeatures, setCatalogFeatures] = useState<CatalogDeskFeatures>({
    vars: [],
    countryLang: [],
    caps: {},
  });

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    (async () => {
      const [p, pk, sip, ivr, langs, catalog, dial] = await Promise.allSettled([
        api.profileNames(),
        api.packs(),
        api.sip(),
        api.listIvr(),
        loadLanguageCatalog(),
        getScriptCatalog(),
        api.dialCatalog(),
      ]);
      if (cancelled) return;
      if (p.status === "fulfilled") setProfiles(p.value);
      if (pk.status === "fulfilled" && pk.value.packs?.length) setPacks(pk.value.packs);
      if (sip.status === "fulfilled") setSips(sip.value.profiles ?? []);
      if (ivr.status === "fulfilled") {
        setIvrs(
          (ivr.value.scripts ?? []).map((item) => ({
            slug: item.slug,
            name: item.name || item.slug,
          })),
        );
      }
      if (langs.status === "fulfilled") {
        if (langs.value.languages.length) setLanguages(langs.value.languages);
        setCountryLanguage(langs.value.countryLanguage);
      }
      if (dial.status === "fulfilled" && dial.value.length) setCountries(dial.value);
      if (catalog.status === "fulfilled") {
        const features = catalogDeskFeatures(catalog.value);
        setCatalogFeatures(features);
        if (catalog.value.pack_files?.length) setPackFiles(catalog.value.pack_files);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active]);

  const sipId = effectiveSipId(form.sip, sips);

  useEffect(() => {
    if (!active || !sipId) {
      setCliKinds(null);
      return;
    }
    let cancelled = false;
    // Unknown until this SIP answers. A failed GET stays null so all four toggles stay enabled.
    setCliKinds(null);
    void api.cliKinds(sipId).then(
      (value) => {
        if (!cancelled) setCliKinds(normalizeCliKinds(value.kinds));
      },
      () => {
        if (!cancelled) setCliKinds(null);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [active, sipId]);

  useEffect(() => {
    if (!active) return;
    setLanguageTouched(false);
    setCidCountryPick("");
    setCidKindState("optimal");
    if (!prefill) {
      setForm(EMPTY_CALL_SETUP);
      return;
    }
    setForm((prev) => ({ ...prev, ...setupToForm(prefill) }));
  }, [active, prefill]);

  const patch = (partial: Partial<CallSetupFormState>) => setForm((prev) => ({ ...prev, ...partial }));

  const otpLength = useMemo(() => {
    const n = Number.parseInt(form.digits, 10);
    return Number.isInteger(n) ? n : NaN;
  }, [form.digits]);

  function onAdvance(event: KeyboardEvent<HTMLElement>) {
    if (event.key !== "Enter") return;
    if (event.currentTarget instanceof HTMLTextAreaElement && !event.ctrlKey) return;
    event.preventDefault();
    const id = event.currentTarget.getAttribute("data-field");
    if (id === "digits") {
      void submitDial();
      return;
    }
    const index = CALL_SETUP_ADVANCE.indexOf(id as (typeof CALL_SETUP_ADVANCE)[number]);
    const next = CALL_SETUP_ADVANCE[index + 1];
    if (!next) return;
    const node = document.querySelector<HTMLElement>(`[data-field="${next}"]`);
    node?.focus();
  }

  async function submitDial(event?: FormEvent): Promise<boolean> {
    event?.preventDefault();
    if (!scriptReady || scriptChecking) {
      toast(scriptReadyReason || SCRIPT_NOT_READY_HINT, "warn");
      return false;
    }
    const countryNow = form.country.trim() || inferCountry(form.dest, countries);
    const trunkNow = trunkCountryBlock(
      sips,
      form.sip,
      countryNow,
      countryByIso(countryNow, countries)?.calling_code ?? "",
    );
    const profileNow = parseCountryList(form.allowed_countries);
    if (trunkNow) {
      toast(trunkNow, "warn");
      return false;
    }
    if (countryNow && profileNow.length && !profileNow.includes(countryNow)) {
      toast(`Profile does not allow destination country ${countryNow}`, "warn");
      return false;
    }
    if (!form.dest.trim()) {
      toast("Customer number is required", "error");
      return false;
    }
    if (!form.name.trim()) {
      toast("Customer name is required", "error");
      return false;
    }
    if (!form.cid.trim()) {
      toast("Caller ID is required", "error");
      return false;
    }
    if (!Number.isInteger(otpLength) || otpLength < 1 || otpLength > 12) {
      toast("OTP length must be an integer 1–12", "error");
      return false;
    }
    const body = formToCallSetup(form, otpLength, languageTouched);
    setBusy(true);
    const ok = await run(() => api.originate(body), "Dial failed");
    setBusy(false);
    if (ok) onDialed?.();
    return ok;
  }

  async function refreshProfileNames() {
    const listed = await api.profileNames().catch(() => null);
    if (listed) setProfiles(listed);
  }

  async function saveProfile(): Promise<boolean> {
    const name = form.profile.trim();
    if (!name) {
      toast("Profile name is required", "error");
      return false;
    }
    const scripts = buildScripts(form);
    setBusy(true);
    const ok = await run(
      () =>
        api.saveProfile({
          name,
          dest: form.dest.trim() || undefined,
          customer_name: form.name.trim() || undefined,
          cli: form.cid.trim() || undefined,
          sip_gateway_id: form.sip || undefined,
          caller_name: form.speak_as.trim() || undefined,
          business_name: form.business.trim() || undefined,
          purpose: form.purpose.trim() || undefined,
          otp_length: Number.isInteger(otpLength) ? otpLength : 6,
          expected_code: form.known_otp.trim() || "",
          pack: form.pack,
          ivr_id: form.ivr_id.trim(),
          scripts,
          spoken_language: form.language,
          lang: form.language,
          country: form.country.trim(),
          allowed_countries: parseCountryList(form.allowed_countries),
          script_vars: form.script_vars,
          identity_replay: form.identity_replay,
          avmd_enabled: form.avmd,
          recordings_enabled: form.record,
        }),
      "Save profile failed",
    );
    setBusy(false);
    if (ok) {
      toast("Profile saved", "ok");
      await refreshProfileNames();
    }
    return ok;
  }

  async function deleteProfile(): Promise<boolean> {
    const name = form.profile.trim();
    if (!name) {
      toast("Profile name is required", "error");
      return false;
    }
    setBusy(true);
    const ok = await run(() => api.deleteProfile(name), "Delete profile failed");
    setBusy(false);
    if (ok) {
      toast("Profile deleted", "ok");
      patch({ profile: "" });
      await refreshProfileNames();
    }
    return ok;
  }

  async function randomCid(country: string) {
    const iso = country.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(iso)) {
      toast("Pick a CID country", "error");
      return;
    }
    try {
      const result = await api.randomCid(iso, cidKindForCountry(cidKind, iso, cliKinds), sipId);
      const cli = typeof result.cli === "string" ? result.cli.trim() : "";
      if (!cli) {
        toast("Random CID failed", "error");
        return;
      }
      patch({ cid: cli });
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail.trim() : "";
      toast(detail || "Random CID failed", "error");
    }
  }

  async function nextCid() {
    await run(async () => {
      const result = await api.nextCid();
      patch({ cid: result.cli });
    }, "No next CLI in the rotation pool");
  }

  async function loadProfile(name: string) {
    patch({ profile: name });
    if (!name.trim()) return;
    try {
      const found = await api.profile(name.trim());
      // Non-empty spoken_language or lang is a pick, including en. Both empty leaves dest to decide.
      const picked = Boolean((found.spoken_language ?? "").trim() || (found.lang ?? "").trim());
      setLanguageTouched(picked);
      setCidCountryPick("");
      setForm((prev) => ({ ...prev, ...profileToForm(found), profile: found.name || name }));
      void loadPackLines(found.pack || form.pack || "default");
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        toast("Profile not found", "error");
        return;
      }
      toast(err instanceof Error ? err.message : "Load profile failed", "error");
    }
  }

  function setStage(stage: string) {
    // Show the existing pack line (or this call's override) — never a
    // blank box (operator was editing blindly).
    const line = form.scripts[stage] ?? packLines[stage] ?? "";
    patch({ stage, line });
  }

  function setLanguage(code: string) {
    const next = code.trim();
    if (!next) {
      setLanguageTouched(false);
      patch({ language: "en" });
      return;
    }
    setLanguageTouched(true);
    patch({ language: next });
  }

  function setCountry(iso2: string) {
    patch({ country: iso2.trim().toUpperCase() });
  }

  function setSip(next: string) {
    const ok = new Set(
      countriesForSip(countries, sips, next).map((row) => row.iso2.toUpperCase()),
    );
    const country = form.country.trim().toUpperCase();
    const pick = cidCountryPick.trim().toUpperCase();
    if (pick && !ok.has(pick)) setCidCountryPick("");
    patch(country && !ok.has(country) ? { sip: next, country: "" } : { sip: next });
  }

  function setCidCountry(iso2: string) {
    const iso = iso2.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(iso)) return;
    setCidCountryPick(iso);
  }

  function addProfileCountry(iso2: string) {
    const iso = iso2.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(iso)) return;
    setForm((prev) => {
      const list = parseCountryList(prev.allowed_countries);
      if (list.includes(iso)) return prev;
      return { ...prev, allowed_countries: [...list, iso].join(", ") };
    });
  }

  function removeProfileCountry(iso2: string) {
    const drop = iso2.trim().toUpperCase();
    if (!drop) return;
    setForm((prev) => {
      const list = parseCountryList(prev.allowed_countries);
      if (!list.includes(drop)) return prev;
      return { ...prev, allowed_countries: list.filter((item) => item !== drop).join(", ") };
    });
  }

  function insertVar(token: string) {
    const name = token.trim();
    if (!name || /secret|password|otp|pin|token/i.test(name)) return;
    const chip = `{{${name}}}`;
    const line = form.line.includes(chip) ? form.line : `${form.line}${form.line && !form.line.endsWith(" ") ? " " : ""}${chip}`;
    setLine(line);
  }

  function setScriptVar(key: string, value: string) {
    const name = key.trim();
    if (!name || /secret|password|api_key|token/i.test(name)) return;
    patch({ script_vars: { ...form.script_vars, [name]: value } });
  }

  function removeScriptVar(key: string) {
    const next = { ...form.script_vars };
    delete next[key];
    patch({ script_vars: next });
  }

  function setLine(line: string) {
    patch({ line, scripts: { ...form.scripts, [form.stage]: line } });
  }

  const scriptOptions: ScriptOption[] = useMemo(
    () => mergeScriptOptions(packs, ivrs),
    [packs, ivrs],
  );
  const scriptValue = formToScriptValue(form.pack, form.ivr_id);

  useEffect(() => {
    if (!active) return;
    const { kind, slug } = parseScriptValue(scriptValue);
    if (kind !== "ivr" || !slug) {
      setScriptReady(true);
      setScriptReadyReason(null);
      setScriptHasCollect(true);
      setScriptChecking(false);
      return;
    }
    let cancelled = false;
    setScriptChecking(true);
    setScriptReady(false);
    (async () => {
      try {
        const script = await getIvrScript(slug);
        const graph = script.graph;
        const sparse = graphIsSparse(graph);
        const missingStart = !graphHasStart(graph);
        let ok = !sparse && !missingStart;
        if (ok) {
          const result = await validateScript({ kind: "ivr", graph });
          ok = result.ok;
        }
        if (cancelled) return;
        setScriptReady(ok);
        setScriptReadyReason(ok ? null : SCRIPT_NOT_READY_HINT);
        setScriptHasCollect(graphHasCollect(graph));
      } catch {
        if (cancelled) return;
        setScriptReady(false);
        setScriptReadyReason(SCRIPT_NOT_READY_HINT);
        setScriptHasCollect(false);
      } finally {
        if (!cancelled) setScriptChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active, scriptValue]);

  function setScript(value: string) {
    const next = scriptValueToForm(value);
    patch(next);
    const pack = next.pack || form.pack || "default";
    void loadPackLines(pack);
  }

  async function loadPackLines(pack: string) {
    const slug = (pack || "default").trim() || "default";
    try {
      const result = await api.scripts(slug);
      setPackLines(result.stages ?? {});
      // Seed the visible line from the pack when this call has no override.
      setForm((prev) => {
        const existing = prev.scripts[prev.stage];
        if (typeof existing === "string" && existing.trim()) return prev;
        const line = result.stages?.[prev.stage] ?? "";
        if (!line || prev.line === line) return prev;
        return { ...prev, line };
      });
    } catch {
      setPackLines({});
    }
  }

  useEffect(() => {
    void loadPackLines(form.pack || "default");
    // Only on mount / first pack — later switches go through setScript.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sipCountries = useMemo(
    () => countriesForSip(countries, sips, form.sip),
    [countries, sips, form.sip],
  );
  const effectiveCountry = form.country.trim() || inferCountry(form.dest, countries);
  const followed = (cidCountryPick || effectiveCountry).trim().toUpperCase();
  const cidCandidate = /^[A-Z]{2}$/.test(followed) ? followed : "GB";
  const cidCountry = sipCountries.some((row) => row.iso2 === cidCandidate) ? cidCandidate : "";

  useEffect(() => {
    const ok = new Set(sipCountries.map((row) => row.iso2.toUpperCase()));
    setForm((prev) => {
      const country = prev.country.trim().toUpperCase();
      if (!country || ok.has(country)) return prev;
      return { ...prev, country: "" };
    });
    setCidCountryPick((prev) => {
      const pick = prev.trim().toUpperCase();
      if (!pick || ok.has(pick)) return prev;
      return "";
    });
  }, [sipCountries, form.country, cidCountryPick]);
  // Sheet or country can drop the selected class. Stick on Optimal so it does not return later.
  const cidKindShown = cidKindForCountry(cidKind, cidCountry, cliKinds);
  if (cidKindShown !== cidKind) setCidKindState(cidKindShown);

  function setCidKind(next: CidKind) {
    if (!isCidKindEnabled(next, cidCountry, cliKinds)) return;
    setCidKindState(next);
  }

  const trunkBlock = trunkCountryBlock(
    sips,
    form.sip,
    effectiveCountry,
    countryByIso(effectiveCountry, countries)?.calling_code ?? "",
  );
  const profileCountries = parseCountryList(form.allowed_countries);
  const profileBlock =
    effectiveCountry && profileCountries.length && !profileCountries.includes(effectiveCountry)
      ? `Profile does not allow destination country ${effectiveCountry}`
      : null;
  const dialReason = !scriptReady ? scriptReadyReason : trunkBlock || profileBlock;
  const suggestLang = hintedLanguage(
    effectiveCountry,
    languages.map((item: { code: string }) => item.code),
    countryLanguage,
  );

  return {
    form,
    patch,
    profiles,
    packs,
    scriptOptions,
    scriptValue,
    setScript,
    sips,
    busy,
    otpLength,
    stages: packFiles.length ? packFiles : [...STAGE_FILES],
    languages,
    countries,
    sipCountries,
    catalogFeatures,
    inferredCountry: inferCountry(form.dest, countries),
    effectiveCountry,
    trunkBlock,
    profileBlock,
    dialReason,
    dialBlocked: busy || !scriptReady || scriptChecking || Boolean(trunkBlock || profileBlock),
    suggestLang,
    languageTouched,
    willSpeak: languageTouched ? form.language.trim() : suggestLang,
    setLanguage,
    setCountry,
    setSip,
    cidCountry,
    setCidCountry,
    cidKind: cidKindShown,
    setCidKind,
    cidKindEnabled: (kind: CidKind) => isCidKindEnabled(kind, cidCountry, cliKinds),
    profileCountries,
    addProfileCountry,
    removeProfileCountry,
    insertVar,
    setScriptVar,
    removeScriptVar,
    onAdvance,
    submitDial,
    saveProfile,
    deleteProfile,
    randomCid,
    nextCid,
    loadProfile,
    setStage,
    setLine,
    scriptReady,
    scriptReadyReason,
    scriptHasCollect,
    scriptChecking,
  };
}

export type CallSetupController = ReturnType<typeof useCallSetup>;

function effectiveSipId(
  pinned: string,
  sips: readonly { sofia_gateway_id: string; active?: boolean }[],
): string {
  const field = pinned.trim();
  if (field) return field;
  return sips.find((item) => item.active)?.sofia_gateway_id.trim() ?? "";
}

const CID_CLASSES = ["landline", "mobile", "premium"] as const;

function normalizeCliKinds(raw: unknown): Record<string, string[]> | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const out: Record<string, string[]> = {};
  for (const [iso, classes] of Object.entries(raw)) {
    const key = iso.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(key) || !Array.isArray(classes)) continue;
    out[key] = classes
      .map((item) => String(item).trim().toLowerCase())
      .filter((item) => (CID_CLASSES as readonly string[]).includes(item));
  }
  return out;
}

function isCidKindEnabled(
  kind: CidKind,
  country: string,
  kinds: Record<string, string[]> | null,
): boolean {
  if (kind === "optimal") return true;
  if (!kinds) return true;
  return (kinds[country.trim().toUpperCase()] ?? []).includes(kind);
}

function cidKindForCountry(
  kind: CidKind,
  country: string,
  kinds: Record<string, string[]> | null,
): CidKind {
  return isCidKindEnabled(kind, country, kinds) ? kind : "optimal";
}

{
  const sheet = { GB: ["mobile"] };
  if (cidKindForCountry("premium", "GB", sheet) !== "optimal") throw new Error("cid kind fallback");
  if (cidKindForCountry("mobile", "DE", sheet) !== "optimal") throw new Error("cid kind missing country");
  if (!isCidKindEnabled("premium", "GB", null)) throw new Error("cid kind fail-open");
  if (!isCidKindEnabled("optimal", "", sheet)) throw new Error("optimal always on");
  if (isCidKindEnabled("landline", "GB", sheet)) throw new Error("cid kind deny");
  const empty: Record<string, string[]> = {};
  if (normalizeCliKinds({}) === null) throw new Error("empty kinds is a sheet");
  if (isCidKindEnabled("landline", "GB", empty)) throw new Error("empty sheet denies landline");
  if (isCidKindEnabled("mobile", "GB", empty)) throw new Error("empty sheet denies mobile");
  if (isCidKindEnabled("premium", "GB", empty)) throw new Error("empty sheet denies premium");
  if (!isCidKindEnabled("optimal", "GB", empty)) throw new Error("empty sheet keeps optimal");
  const trunks = [
    { sofia_gateway_id: "aa", active: true },
    { sofia_gateway_id: "bb" },
  ];
  if (effectiveSipId("bb", trunks) !== "bb") throw new Error("sip field wins");
  if (effectiveSipId("", trunks) !== "aa") throw new Error("active trunk");
  if (effectiveSipId("  ", []) !== "") throw new Error("no invented sip");
}
